import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Bell, RefreshCw, CheckCheck, AlertCircle, Package, MessageSquare, X, Ban, AlertTriangle } from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { API } from "@/config";

const CATEGORY_LABELS = {
  new_order: "New Order",
  change_request: "Change Request",
  cancellation: "Cancellation",
  payment_issue: "Payment Issue",
  failed_message: "Failed Message",
};

const CATEGORY_ICON = {
  new_order: Package,
  change_request: RefreshCw,
  cancellation: Ban,
  payment_issue: AlertTriangle,
  failed_message: MessageSquare,
};

const CATEGORY_COLOR = {
  new_order: "text-emerald-400",
  change_request: "text-[#FFD166]",
  cancellation: "text-red-400",
  payment_issue: "text-orange-400",
  failed_message: "text-red-400",
};

function relativeTime(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function NotifRow({ notif, onRead, onResolve }) {
  const Icon = CATEGORY_ICON[notif.category] || Bell;
  const colorCls = CATEGORY_COLOR[notif.category] || "text-[#B9C8D5]";

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border transition-colors cursor-pointer hover:bg-white/5 ${
        !notif.is_read ? "bg-[#FFD166]/5 border-[#FFD166]/20" : "border-[#FFD166]/8 bg-transparent"
      }`}
      onClick={() => onRead(notif)}
      data-testid={`notif-center-row-${notif.id}`}
    >
      <span className={`mt-0.5 shrink-0 ${colorCls}`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center flex-wrap gap-2 mb-0.5">
          <span className="text-sm font-semibold text-[#F6F1E4]">{notif.title}</span>
          {notif.action_required && notif.status !== "resolved" && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30">
              Action needed
            </span>
          )}
          {notif.status === "resolved" && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              Resolved
            </span>
          )}
          {!notif.is_read && (
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFD166] shrink-0" />
          )}
        </div>
        {notif.order_number && (
          <div className="text-xs text-[#B9C8D5]">Order #{notif.order_number}{notif.customer_name ? ` — ${notif.customer_name}` : ""}</div>
        )}
        <p className="text-xs text-[#B9C8D5] mt-0.5">{notif.message}</p>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[10px] text-[#B9C8D5]/50">{relativeTime(notif.created_at)}</span>
          <div className="flex items-center gap-2">
            {notif.action_required && notif.status !== "resolved" && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onResolve(notif); }}
                className="text-[10px] px-2 py-0.5 rounded border border-[#FFD166]/30 text-[#B9C8D5] hover:text-[#FFD166] hover:border-[#FFD166]/60"
              >
                Mark Resolved
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function NotificationCenter() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initTab = searchParams.get("action") === "true" ? "action" : "all";
  const initCategory = searchParams.get("category") || "";

  const [tab, setTab] = useState(initTab);
  const [categoryFilter, setCategoryFilter] = useState(initCategory);
  const [notifs, setNotifs] = useState([]);
  const [counts, setCounts] = useState({ unread: 0, action_total: 0 });
  const [loading, setLoading] = useState(true);

  const fetchCounts = async () => {
    const { data } = await axios.get(`${API}/admin/notifications/counts`).catch(() => ({ data: { unread: 0, action_total: 0 } }));
    setCounts(data);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === "unread") params.is_read = false;
      if (tab === "action") params.action_required = true;
      if (tab === "resolved") params.status = "resolved";
      if (categoryFilter) params.category = categoryFilter;
      const { data } = await axios.get(`${API}/admin/notifications`, { params });
      setNotifs(data.notifications || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); fetchCounts(); }, [tab, categoryFilter]);

  const handleRead = async (notif) => {
    if (!notif.is_read) {
      await axios.patch(`${API}/admin/notifications/${notif.id}/read`).catch(() => {});
      setNotifs((n) => n.map((x) => x.id === notif.id ? { ...x, is_read: true } : x));
      fetchCounts();
    }
    if (notif.action_url) {
      const url = new URL(notif.action_url, window.location.origin);
      nav(url.pathname + url.search);
    }
  };

  const handleResolve = async (notif) => {
    await axios.patch(`${API}/admin/notifications/${notif.id}/resolve`).catch(() => {});
    setNotifs((n) => n.map((x) => x.id === notif.id ? { ...x, status: "resolved", action_required: false } : x));
    fetchCounts();
  };

  const markAllRead = async () => {
    await axios.patch(`${API}/admin/notifications/read-all`).catch(() => {});
    setNotifs((n) => n.map((x) => ({ ...x, is_read: true })));
    setCounts((c) => ({ ...c, unread: 0 }));
  };

  return (
    <div data-testid="notification-center-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Notification Center</h1>
          <p className="text-sm text-[#B9C8D5] mt-1">
            {counts.unread > 0 ? `${counts.unread} unread` : "All caught up"} · {counts.action_total > 0 ? `${counts.action_total} action needed` : "no actions needed"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {counts.unread > 0 && (
            <Btn variant="ghost" onClick={markAllRead} data-testid="notif-center-mark-all-read">
              <CheckCheck className="w-4 h-4" /> Mark all read
            </Btn>
          )}
          <Btn variant="ghost" onClick={load}>
            <RefreshCw className="w-4 h-4" /> Refresh
          </Btn>
        </div>
      </div>

      <Card className="p-4">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-4 border-b border-[#FFD166]/10 pb-3">
          {[["all", "All"], ["unread", `Unread${counts.unread > 0 ? ` (${counts.unread})` : ""}`], ["action", `Action Needed${counts.action_total > 0 ? ` (${counts.action_total})` : ""}`], ["resolved", "Resolved"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tab === key ? "bg-[#FFD166] text-[#040A0F] border-[#FFD166] font-semibold" : "bg-transparent text-[#B9C8D5] border-[#FFD166]/20 hover:border-[#FFD166]/50"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {[["", "All Categories"], ...Object.entries(CATEGORY_LABELS)].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setCategoryFilter(key)}
              className={`text-xs px-2.5 py-1 rounded border transition-colors ${categoryFilter === key ? "bg-white/10 text-[#F6F1E4] border-white/30" : "text-[#B9C8D5] border-[#FFD166]/10 hover:border-[#FFD166]/30"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-12 text-[#B9C8D5]">Loading…</div>
        ) : notifs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3 text-[#B9C8D5]">
            <Bell className="w-10 h-10 opacity-20" />
            <p className="text-sm">No notifications in this view</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifs.map((n) => (
              <NotifRow key={n.id} notif={n} onRead={handleRead} onResolve={handleResolve} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
